// Implements: F-MCK-001, F-MCK-002, F-MCK-011, F-MCK-016, F-MCK-017, F-UIX-009
/**
 * The `mockups` module's client, on top of the single HTTP exit.
 *
 * Paths are the annex's (`router_mockups.py`): the gallery is `GET /mockups`
 * and the trash is `GET /mockups/trash`, a literal path the backend declares
 * before `/mockups/{mockup_id}`. Identifiers are escaped even though the
 * backend issues UUIDs, for the reason `authApi.sessionPath` gives: they come
 * out of a response body.
 *
 * A page is an OBJECT with `items` and `next_cursor`, never a bare array, and
 * a body that lacks `items` is an empty page rather than a crash.
 */

import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { httpRequest } from "../../../shared/api/httpClient";
import { API_BASE } from "../../../shared/api/apiBase";
import { NO_FILTERS, filterParams, scopeParams } from "./galleryFilters";
import type { GalleryFilters } from "./galleryFilters";
import type {
  AssignmentListView,
  FoundedTurnAccepted,
  GalleryCountsView,
  MockupAssignmentView,
  MockupDetailView,
  MockupPageView,
  MockupRole,
  MockupSummary,
  TrashPageView,
} from "../../../types/api";

export const MOCKUPS_PATH = `${API_BASE}/mockups`;
export const TRASH_PATH = `${API_BASE}/mockups/trash`;
/** Mockup v3 P8 (C2): a literal path the backend declares before `/mockups/{mockup_id}`. */
export const COUNTS_PATH = `${API_BASE}/mockups/counts`;

export function mockupPath(mockupId: string): string {
  return `${MOCKUPS_PATH}/${encodeURIComponent(mockupId)}`;
}

/** The query for a page: the filters, then `cursor=…` for the next page; no query at all for the first. */
function withQuery(path: string, cursor: string | undefined, filters: GalleryFilters = NO_FILTERS): string {
  const params = filterParams(filters);
  if (cursor !== undefined) params.set("cursor", cursor);
  const query = params.toString();
  return query === "" ? path : `${path}?${query}`;
}

export async function fetchMockupPage(
  cursor: string | undefined,
  filters: GalleryFilters = NO_FILTERS,
): Promise<MockupPageView> {
  const body = await httpRequest<Partial<MockupPageView>>(withQuery(MOCKUPS_PATH, cursor, filters));
  return { items: body?.items ?? [], next_cursor: body?.next_cursor ?? null };
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

/**
 * The size of each gallery tab for the chosen scope (mockup v3 P8, C2): the counts follow the
 * Ámbito only (P8-3), so the search, the tag, the favourite tab and the order are not sent. An
 * answer that is not three whole counts is refused rather than shown as numbers it does not hold.
 */
export async function fetchGalleryCounts(filters: GalleryFilters): Promise<GalleryCountsView> {
  const query = scopeParams(filters).toString();
  const body = await httpRequest<Partial<Record<keyof GalleryCountsView, unknown>>>(
    query === "" ? COUNTS_PATH : `${COUNTS_PATH}?${query}`,
  );
  if (!isCount(body?.all) || !isCount(body.favorites) || !isCount(body.trash)) {
    throw new ApiError(200, UNKNOWN_ERROR_CODE, "GET /mockups/counts answered no counts", new Headers());
  }
  return { all: body.all, favorites: body.favorites, trash: body.trash };
}

export async function fetchTrashPage(cursor: string | undefined): Promise<TrashPageView> {
  const body = await httpRequest<Partial<TrashPageView>>(withQuery(TRASH_PATH, cursor));
  return { items: body?.items ?? [], next_cursor: body?.next_cursor ?? null };
}

/**
 * Say what the favourite flag must BE, not "flip it": a retry after a lost
 * response or a double tap must not undo the mark (the route says so too).
 */
export async function setMockupFavorite(
  mockupId: string,
  isFavorite: boolean,
): Promise<MockupSummary | undefined> {
  return httpRequest<MockupSummary>(mockupPath(mockupId), {
    method: "PATCH",
    body: { is_favorite: isFavorite },
  });
}

export async function restoreMockup(mockupId: string): Promise<void> {
  await httpRequest<unknown>(`${mockupPath(mockupId)}/restore`, { method: "POST" });
}

/** Irreversible: callers confirm first. Without the flag the backend only trashes. */
export async function deleteMockupPermanently(mockupId: string): Promise<void> {
  await httpRequest<unknown>(`${mockupPath(mockupId)}?permanent=true`, { method: "DELETE" });
}

/**
 * Move a mockup to another scope (PUT, audited by the backend: F-MCK-019).
 * `personal` carries no id: the backend reads that as the caller's own scope.
 */
export async function changeMockupScope(
  mockupId: string,
  scopeKind: string,
  scopeId: string | null,
): Promise<MockupSummary | undefined> {
  return httpRequest<MockupSummary>(`${mockupPath(mockupId)}/scope`, {
    method: "PUT",
    body: scopeId === null ? { scope_kind: scopeKind } : { scope_kind: scopeKind, scope_id: scopeId },
  });
}

export interface CreateMockupInput {
  readonly scopeKind: string;
  /** `null` for `personal`: the backend reads that as the caller's own scope. */
  readonly scopeId: string | null;
  /** The first message: the mockup's first turn. */
  readonly text: string;
  /** `null` asks the backend to suggest one (F-MCK-005). */
  readonly name: string | null;
  /** The client's own turn id: the server's idempotency key (a repeat answers the same ids). */
  readonly turnId: string;
  /** The first message's reference image as plain base64 (F-CNV-012); absent when none is attached. */
  readonly imageBase64?: string | undefined;
}

/**
 * A mockup is born with its first turn, in one call (`POST /mockups/conversation`, 202; 7.6).
 *
 * All or nothing on the server: a refusal (usage, rate, scope, capacity) leaves no mockup. Paid,
 * so never retried (`retry: false`, checklist 13): a lost response reaches the person, and the
 * turn id chosen by the caller makes a deliberate press again harmless (the same three ids).
 */
export async function createMockup(input: CreateMockupInput): Promise<FoundedTurnAccepted | undefined> {
  return httpRequest<FoundedTurnAccepted>(`${MOCKUPS_PATH}/conversation`, {
    method: "POST",
    body: {
      scope_kind: input.scopeKind,
      ...(input.scopeId === null ? {} : { scope_id: input.scopeId }),
      ...(input.name === null ? {} : { name: input.name }),
      text: input.text,
      turn_id: input.turnId,
      ...(input.imageBase64 === undefined ? {} : { image_base64: input.imageBase64 }),
    },
    retry: false,
  });
}

/**
 * Rename a mockup (`PATCH`, F-MCK-006): the server trims and bounds the name and answers the
 * mockup as it now is. Never retried: a lost answer is shown, and the person decides.
 */
export async function renameMockup(mockupId: string, name: string): Promise<MockupSummary | undefined> {
  return httpRequest<MockupSummary>(mockupPath(mockupId), {
    method: "PATCH",
    body: { name },
    retry: false,
  });
}

/** The address of one tag of a mockup: the tag is a path segment, so it is escaped. */
function tagPath(mockupId: string, tag: string): string {
  return `${mockupPath(mockupId)}/tags/${encodeURIComponent(tag)}`;
}

/** Add a tag (`PUT`, 204, F-MCK-015): the server normalises it; a repeat is harmless. */
export async function addMockupTag(mockupId: string, tag: string): Promise<void> {
  await httpRequest<unknown>(tagPath(mockupId, tag), { method: "PUT", retry: false });
}

/** Remove a tag (`DELETE`, 204, F-MCK-015). */
export async function removeMockupTag(mockupId: string, tag: string): Promise<void> {
  await httpRequest<unknown>(tagPath(mockupId, tag), { method: "DELETE", retry: false });
}

/** Move a mockup to the trash (`DELETE` without `permanent`): restorable until it is purged. */
export async function trashMockup(mockupId: string): Promise<void> {
  await httpRequest<unknown>(mockupPath(mockupId), { method: "DELETE" });
}

// --- Mockup v3 P4: duplicate, transfer and collaborators (F-MCK-014, F-MCK-021) ---

/** A call that must answer a body and did not is an error the page shows, never `undefined`. */
function answered<T>(body: T | undefined, call: string): T {
  if (body === undefined) throw new ApiError(200, UNKNOWN_ERROR_CODE, `${call} answered with no body`, new Headers());
  return body;
}

/**
 * Copy a mockup with its current version (`POST /mockups/{id}/duplicate`, 201; default P4-2).
 * Not idempotent: a retry after a lost answer would make a second copy, so never retried.
 */
export async function duplicateMockup(mockupId: string): Promise<MockupDetailView> {
  const body = await httpRequest<MockupDetailView>(`${mockupPath(mockupId)}/duplicate`, {
    method: "POST",
    retry: false,
  });
  return answered(body, "POST duplicate");
}

/** Hand the mockup to another person (`PUT /mockups/{id}/owner`); never retried. */
export async function transferOwnership(mockupId: string, userId: string): Promise<MockupDetailView> {
  const body = await httpRequest<MockupDetailView>(`${mockupPath(mockupId)}/owner`, {
    method: "PUT",
    body: { user_id: userId },
    retry: false,
  });
  return answered(body, "PUT owner");
}

function assignmentPath(mockupId: string, userId: string): string {
  return `${mockupPath(mockupId)}/assignments/${encodeURIComponent(userId)}`;
}

/** The explicit assignments of one mockup; the owner is not among them unless assigned. */
export async function fetchAssignments(
  mockupId: string,
  signal?: AbortSignal,
): Promise<readonly MockupAssignmentView[]> {
  const body = await httpRequest<Partial<AssignmentListView>>(`${mockupPath(mockupId)}/assignments`, { signal });
  return body?.assignments ?? [];
}

/** Give one person a role on the mockup (owner only on the server). */
export async function assignRole(
  mockupId: string,
  userId: string,
  role: Exclude<MockupRole, "owner">,
): Promise<MockupAssignmentView> {
  const body = await httpRequest<MockupAssignmentView>(assignmentPath(mockupId, userId), {
    method: "PUT",
    body: { role },
    retry: false,
  });
  return answered(body, "PUT assignment");
}

/** Take one person's explicit access away (204). */
export async function revokeAssignment(mockupId: string, userId: string): Promise<void> {
  await httpRequest<unknown>(assignmentPath(mockupId, userId), { method: "DELETE", retry: false });
}
